'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  Music,
  MapPin,
  Calendar,
  Clock,
} from 'lucide-react'
import Image from 'next/image'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import type { DemoArtist, DemoEvent, DemoSong } from '../demo-types'
import { SocialIcon } from './social-icon'

interface ArtistProfileProps {
  artist: DemoArtist
  event: DemoEvent
  featuredSongs: DemoSong[]
}

export function ArtistProfile({ artist, event, featuredSongs }: ArtistProfileProps) {
  const t = useTranslations('demo')
  const handleSongTap = (_song: DemoSong) => {
    toast(t('requestWhenStarts'), {
      icon: <Music className="h-4 w-4 text-purple-400" />,
    })
  }

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-950">
      {/* Cover Image Hero */}
      <div className="relative h-40 overflow-hidden">
        {artist.coverImageUrl ? (
          <Image
            src={artist.coverImageUrl}
            alt={`${artist.name} cover`}
            fill
            className="object-cover"
            unoptimized
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-900 via-fuchsia-900 to-pink-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />

        {/* Avatar + Name over cover */}
        <div className="absolute bottom-4 left-4 flex items-end gap-3">
          <Avatar className="h-16 w-16 ring-2 ring-purple-500/50 border-2 border-zinc-950 shrink-0">
            <AvatarImage src={artist.profileImageUrl || ''} />
            <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-xl">
              {artist.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="pb-0.5">
            <h1 className="font-bold text-white text-xl drop-shadow-lg">{artist.name}</h1>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 pb-6 space-y-4">
        {/* Social Links Row */}
        <div className="flex items-center gap-2">
          {artist.socialLinks.map(link => (
            <button
              key={link.id}
              className="p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
              title={link.displayName || link.platform}
            >
              <SocialIcon platform={link.platform} />
            </button>
          ))}
        </div>

        {/* Bio Card */}
        <Card className="border-zinc-800 bg-zinc-900/50">
          <CardContent className="p-4">
            <p className="text-sm text-zinc-300 leading-relaxed">{artist.bio}</p>
          </CardContent>
        </Card>

        {/* Featured Songs Grid */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Music className="h-4 w-4 text-purple-400" />
            <span className="font-semibold text-white text-sm">{t('featuredSongs')}</span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {featuredSongs.map(song => (
              <button
                key={song.id}
                onClick={() => handleSongTap(song)}
                className="flex items-center gap-2.5 p-2.5 rounded-lg bg-zinc-900/80 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/80 transition-colors text-left group"
              >
                <SongArtwork
                  src={song.artworkUrl}
                  alt={song.title}
                  size={40}
                  className="h-10 w-10 rounded shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate group-hover:text-purple-300 transition-colors">
                    {song.title}
                  </p>
                  <p className="text-[11px] text-zinc-500 truncate">{song.artist}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming Event Card */}
        <Card className="border-purple-500/30 bg-gradient-to-br from-purple-950/50 to-fuchsia-950/50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-400" />
                <span className="font-semibold text-white text-sm">{t('tonightsEvent')}</span>
              </div>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 text-xs">
                {t('startingSoonBadge')}
              </Badge>
            </div>
            <h3 className="font-semibold text-white mb-2">{event.title}</h3>
            <div className="space-y-1.5">
              {event.venue && (
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span>{event.venue}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {event.startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-2">
          <p className="text-xs text-zinc-600">
            {t('poweredBy')} <span className="text-pink-500">iPerform.live</span>
          </p>
        </div>
      </div>
    </div>
  )
}
